import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Card,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import api, { API_BASE_URL } from "../../src/utils/api";

const Visits = ({ violation_id, currentUserId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get(`/getEvents`);
        const filteredEvents = response.data.filter(event => event.violation_id === Number(violation_id));
        setEvents(filteredEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    if (violation_id) {
      fetchEvents();
    }
  }, [violation_id]);

  const fetchComments = async (eventId) => {
    setIsCommentsLoading(true);
    try {
      const response = await api.get(`/getViolationComments/${violation_id}/${eventId}`);
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  const handleOpenComments = (eventId) => {
    setSelectedEventId(eventId);
    setIsModalOpen(true);
    fetchComments(eventId);
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setFilePreview(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
  });

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("violation_id", violation_id);
      formData.append("event_id", selectedEventId);
      formData.append("user_id", currentUserId);
      formData.append("comment_text", newComment);
      formData.append("is_internal", 0);
      if (selectedFile) {
        formData.append("upload_urls", selectedFile);
      }

      await api.post(`/addViolationComment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setComments((prev) => [
        {
          comment_text: newComment,
          sent_at: new Date().toISOString(),
          user_name: "You",
          user_lastname: "",
          upload_urls: selectedFile ? selectedFile.name : null,
        },
        ...prev,
      ]);

      setNewComment("");
      setSelectedFile(null);
      setFilePreview(null);
      
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to add comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Visits Related to Violation ID: {violation_id}
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : events.length === 0 ? (
        <Typography>No visits found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Violation ID</TableCell>
                <TableCell>Event ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map(event => (
                <TableRow key={event.id}>
                  <TableCell>{event.violation_id}</TableCell>
                  <TableCell>{event.id}</TableCell>
                  <TableCell>{event.title}</TableCell>
                  <TableCell>{event.description}</TableCell>
                  <TableCell>{new Date(event.start_time).toLocaleString()}</TableCell>
                  <TableCell>{new Date(event.end_time).toLocaleString()}</TableCell>
                  <TableCell>{event.created_by_user_id}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleOpenComments(event.id)}
                    >
                      View/Add Comments
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal for Viewing and Adding Comments */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="comments-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 4,
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Typography id="comments-modal" variant="h6" sx={{ mb: 2 }}>
            Comments for Violation ID: {violation_id}, Event ID: {selectedEventId}
          </Typography>

          {isCommentsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
              <CircularProgress />
            </Box>
          ) : comments.length > 0 ? (
            comments.map((comment, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: "1px solid #ddd", borderRadius: "8px" }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {comment.comment_text}
                </Typography>

                {comment.upload_urls && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight="bold">📎 Attached Image:</Typography>
                    <Card sx={{ mt: 1, maxWidth: 200 }}>
                      <img
                        src={`${API_BASE_URL}/violationsimages/${encodeURIComponent(comment.upload_urls?.split(/(\\|\/)/g).pop())}`}
                        alt="Comment Attachment"
                        style={{ width: "100%", borderRadius: "8px" }}
                      />
                    </Card>
                  </Box>
                )}

                <Typography variant="caption" color="textSecondary">
                  Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                  {new Date(comment.sent_at).toLocaleString()}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography>No comments available.</Typography>
          )}

          <TextField
            fullWidth
            label="Add a Comment"
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />

          {/* Dropzone */}
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed #ccc",
              padding: "16px",
              textAlign: "center",
              mb: 2,
              cursor: "pointer",
              backgroundColor: "#f9f9f9",
            }}
          >
            <input {...getInputProps()} />
            <Typography>Drag & Drop or click to select an image</Typography>
          </Box>

          {filePreview && (
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="body2">📎 Image Preview:</Typography>
              <img src={filePreview} alt="Preview" style={{ maxWidth: "100%", maxHeight: 200 }} />
            </Box>
          )}

          <Button fullWidth variant="contained" color="primary" onClick={handleAddComment}>
            Save Comment
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default Visits;
